import { createI18n } from 'vue-i18n'
import fr from '@/locales/fr.json'
import en from '@/locales/en.json'
// Espace perso / parcours connecté : trad isolées pour éviter les conflits de
// merge avec le back-end (le namespace `auth` n'est édité que côté front).
import authFr from '@/locales/auth/fr.json'
import authEn from '@/locales/auth/en.json'

const messagesFr = { ...fr, ...authFr }
const messagesEn = { ...en, ...authEn }

export const SUPPORTED_LOCALES = ['fr', 'en'] as const
export type Locale = (typeof SUPPORTED_LOCALES)[number]
export const DEFAULT_LOCALE: Locale = 'fr'

/** Clé localStorage mémorisant la dernière locale chargée, pour rediriger
 *  l'utilisateur dans sa langue après un retour externe (ex. Stripe). */
export const LOCALE_STORAGE_KEY = 'smerra:locale'

/** Locale persistée (choix manuel ou dernière page chargée), ou `null` si aucune
 *  n'a encore été mémorisée. Le `null` distingue « jamais choisie » (l'URL fait
 *  foi, ex. lien direct /en/…) de « choisie » (préférence collante). */
export function getPreferredLocale(): Locale | null {
  if (typeof localStorage === 'undefined') return null
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
  return SUPPORTED_LOCALES.includes(stored as Locale) ? (stored as Locale) : null
}

/** Mémorise la locale courante (préférence collante). No-op côté SSR. */
export function setStoredLocale(locale: Locale): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // localStorage indisponible (navigation privée) : on ignore.
  }
}

/** Dernière locale chargée (persistée), ou la locale par défaut.
 *  Sûr côté SSR : sans `localStorage`, on retombe sur la valeur par défaut. */
export function getStoredLocale(): Locale {
  return getPreferredLocale() ?? DEFAULT_LOCALE
}

/** Crée une instance i18n. Une nouvelle instance par page lors du SSR. */
export function createI18nInstance(locale: Locale = DEFAULT_LOCALE) {
  return createI18n({
    legacy: false,
    locale,
    fallbackLocale: DEFAULT_LOCALE,
    messages: { fr: messagesFr, en: messagesEn },
  })
}

/** Préfixe une URL relative avec la locale (sauf locale par défaut). */
export function localizedPath(path: string, locale: Locale): string {
  const clean = path.startsWith('/') ? path : '/' + path
  if (locale === DEFAULT_LOCALE) return clean
  return `/${locale}${clean === '/' ? '/' : clean}`
}
